import Logger from '../../utils/logger';
import request from '../../utils/request';
import simpleInterval from '../../utils/simple_interval';

export default class HeartbeatService {
  _player = null;
  _request = null;
  _intervalTimer = null;
  _successCount = 0;
  _failureCount = 0;
  _pendingRequests = [];

  constructor(player, _request = request) {
    this._player = player;
    this._request = _request;
    this._intervalTimer = simpleInterval(this._sendHeartbeat, 10000);
  }

  start() {
    this._intervalTimer.start();
    Logger.debug('Heartbeat service: started');
  }

  async stop() {
    if (!this._intervalTimer.isStarted()) return;

    this._intervalTimer.stop();
    await Promise.all([...this._pendingRequests]);
    Logger.debug('Heartbeat service: stopped');
  }

  sendHeartbeat() {
    if (this._intervalTimer.isStarted()) {
      this._sendHeartbeat(true);
    }
  }

  _sendHeartbeat = (important) => {
    const {
      isPlaying,
      heartbeat: heartbeatUrl,
      headers,
      isLive,
      isChromeCasting,
      adsPlaying,
      offsetFromLive,
      currentTime,
      playerError,
    } = this._player.model;

    if (isChromeCasting) {
      Logger.debug('Heartbeat service: heartbeat not being sent, since client is Chromecasting');
      return;
    }

    if (adsPlaying) {
      Logger.debug('Heartbeat service: heartbeat not being sent, since client is playing ads');
      return;
    }

    if (playerError) {
      Logger.debug('Heartbeat service: heartbeat not being sent, since player error occured');
      return;
    }

    if (!isPlaying && !important) {
      return;
    }

    Logger.debug(`Heartbeat service: sending heartbeat request to ${heartbeatUrl}`);

    const timestamp = isLive ? -1 * offsetFromLive : currentTime;

    const promise = this._request({
      url: heartbeatUrl,
      type: 'POST',
      authorize: true,
      headers: {
        ...headers,
        'Magine-Play-Timestamp': timestamp,
      },
    }).then(
      () => {
        this._successCount += 1;

        this._player.dispatchEvent('heartbeat', { timestamp });
      }, (e) => {
        this._failureCount += 1;
        Logger.warn('Heartbeat service: heartbeat failed', e);

        const response = JSON.parse(e?.extra?.response || null);
        if (response?.error?.code === 552) {
          const error = new Error('HeartbeatError');
          error.details = {
            failCount: this._failureCount,
            extra: e.extra,
          };

          this._player._errorhandlingService?.onCriticalError(error);

          this._player.destroy();
        }
        if (response?.code === 401) {
          this._player._errorhandlingService?.onCriticalError(e);
        }
      },
    ).finally(() => {
      const index = this._pendingRequests.indexOf(promise);
      if (index > -1) {
        this._pendingRequests.splice(index, 1);
      }
    });

    this._pendingRequests.push(request);
  }
}
